import React from 'react';

interface InstitutionalShieldProps {
    toolName?: string;
    category?: string;
}

const InstitutionalShield: React.FC<InstitutionalShieldProps> = ({ toolName = 'Utility', category = 'General' }) => {
    // Advanced Category Normalization
    const normalizedCategory = (() => {
        const cat = (category || 'General').toLowerCase();
        const name = (toolName || 'Utility').toLowerCase();
        if (name.includes('pdf')) return 'PDF';
        if (cat.includes('financial') || cat.includes('fiscal') || cat.includes('tax')) return 'Financial';
        if (cat.includes('marketing') || cat.includes('ai') || cat.includes('business')) return 'Marketing';
        if (cat.includes('developer') || cat.includes('json') || cat.includes('code')) return 'Developer';
        if (cat.includes('security') || cat.includes('password') || cat.includes('crypto')) return 'Security';
        if (cat.includes('text') || cat.includes('content') || cat.includes('utility')) return 'Content';
        return 'General';
    })();

    const getCategoryContent = (cat: string) => {
        switch (cat) {
            case 'Financial':
                return {
                    heading: 'Offline Calculation & Data Privacy',
                    body: `When you're running payroll projections or calculating tax liabilities, you don't want those numbers leaking to third-party tracking scripts. ${toolName} keeps your salary and company revenue figures completely isolated. The underlying math executes right inside your browser tab using standard floating point arithmetic. Because the browser handles the calculations locally, your totals remain accurate without relying on external servers.`,
                    specs: [
                        { layer: 'Math Engine', tech: `Double Precision Arithmetic for ${toolName}`, status: 'Verified' },
                        { layer: 'Calculation Rules', tech: `Localized Calculation Logic for ${toolName}`, status: 'Active' },
                        { layer: 'Rounding Logic', tech: `Standard Banker's Rounding for ${toolName}`, status: 'Verified' },
                        { layer: 'Data Isolation', tech: `Isolated In-Browser RAM for ${toolName}`, status: 'Zero-Trace' }
                    ]
                };
            case 'Content':
                return {
                    heading: 'Local String Management & Unicode Preservation',
                    body: `Sorting lists, cleaning unformatted text, and formatting code blocks on remote servers creates unnecessary processing delays. ${toolName} is engineered to format large text datasets directly within your active browser tab. Our parsing engine supports standard Unicode formats to preserve international characters, ligatures, and layouts intact. Keeping execution entirely client-side ensures your documents remain secure.`,
                    specs: [
                        { layer: 'Text Parser', tech: `Local Parsing Engine for ${toolName}`, status: 'Verified' },
                        { layer: 'Encoding', tech: `Full Unicode UTF-8/16 Support for ${toolName}`, status: 'Universal' },
                        { layer: 'Logic Hub', tech: `Browser Runtime Sandbox for ${toolName}`, status: 'Secure' },
                        { layer: 'Validation', tech: `Immediate String Purge for ${toolName}`, status: 'Active' }
                    ]
                };
            case 'Security':
                return {
                    heading: 'Secure Entropy & Cryptographic Isolation',
                    body: `Generating passwords, verifying JSON Web Tokens, and hashing sensitive credentials on public cloud converters introduces significant security risks. Many online utilities log inputs to external databases. ${toolName} is built for local isolation, tapping directly into the browser's Web Crypto API using window.crypto.getRandomValues. This secure random generator pulls entropy from your operating system, processing sensitive strings within a temporary browser sandbox that clears immediately upon closing the tab.`,
                    specs: [
                        { layer: 'Entropy Source', tech: `CSPRNG Entropy Source for ${toolName}`, status: 'Verified' },
                        { layer: 'Memory Safety', tech: `Temporary RAM Purge for ${toolName}`, status: 'Active' },
                        { layer: 'Processing', tech: `Local Browser Memory for ${toolName}`, status: 'Zero-Trace' },
                        { layer: 'Isolation', tech: `${toolName} Sandbox Context Isolation`, status: 'Verified' }
                    ]
                };
            case 'PDF':
                return {
                    heading: 'In-Memory PDF Handling & Zero Disk Storage',
                    body: `Sharing confidential contracts or financial records with remote PDF tools compromises security. ${toolName} manipulates document files directly within your browser's local memory. Utilizing optimized file readers, the engine splits, merges, and compresses files in temporary browser memory at high speeds. Metadata structures are preserved intact with complete offline security.`,
                    specs: [
                        { layer: 'Binary Parser', tech: `WASM-Accelerated PDF Engine for ${toolName}`, status: 'Verified' },
                        { layer: 'Buffer Mgmt', tech: `Isolated ArrayBuffer Heap for ${toolName}`, status: 'Active' },
                        { layer: 'Data Privacy', tech: `Client-Side PDF Execution for ${toolName}`, status: 'Verified' },
                        { layer: 'Metadata Handling', tech: `XRef Document Preservation for ${toolName}`, status: 'Verified' }
                    ]
                };
            case 'Developer':
                return {
                    heading: 'Browser-Native Serialization & Syntax Parsing',
                    body: `Developer workflows are frequently slowed down by web utilities that require logins or struggle with large payloads. ${toolName} is a high-speed, browser-native tool built to streamline data formatting and syntax inspection. By leveraging AST parsing and running formatting tasks locally, the utility processes complex structures instantly without locking up your browser. There are no server round-trips or remote tracking logs.`,
                    specs: [
                        { layer: 'Parser Engine', tech: `Syntax Tree Traversal for ${toolName}`, status: 'High-Performance' },
                        { layer: 'Logic Integrity', tech: `Local Code Processing for ${toolName}`, status: 'Verified' },
                        { layer: 'Environment', tech: `${toolName} Worker Thread Isolation`, status: 'Secure' },
                        { layer: 'Serialization', tech: `Direct Buffer Handling for ${toolName}`, status: 'Active' }
                    ]
                };
            case 'Marketing':
                return {
                    heading: 'Structured Brand Strategy & Local Analytics',
                    body: `Professional brand positioning requires structured, high-quality content strategies rather than generic templates. ${toolName} generates detailed campaign outlines and plans locally on your machine. All calculations and text processing run in your current browser tab, keeping your proprietary strategies confidential.`,
                    specs: [
                        { layer: 'Analysis Hub', tech: `Structured Content Chaining for ${toolName}`, status: 'Active' },
                        { layer: 'Optimization', tech: `Context-Aware Outlining for ${toolName}`, status: 'Verified' },
                        { layer: 'Output Fidelity', tech: `Corporate Formatting for ${toolName}`, status: 'Standard' },
                        { layer: 'Privacy Layer', tech: `Local Analytics Sandbox for ${toolName}`, status: 'Secure' }
                    ]
                };
            default:
                return {
                    heading: 'Local Data Processing & In-Browser Execution',
                    body: `We believe your sensitive information should remain private. ${toolName} runs entirely inside your browser's local tab memory, processing all inputs directly on your device. Eliminating server-side uploads removes data leak risks and ensures your proprietary files remain strictly on your own computer. Closing the tab wipes the active memory instantly.`,
                    specs: [
                        { layer: 'Logic Engine', tech: `Isolated Sandbox for ${toolName}`, status: 'Verified' },
                        { layer: 'Data Persistence', tech: `Volatile RAM Only for ${toolName}`, status: 'Active' },
                        { layer: 'Entropy Source', tech: `Web Crypto API for ${toolName}`, status: 'Secure' },
                        { layer: 'Audit Integrity', tech: `Client-Side Logic for ${toolName}`, status: 'Verified' }
                    ]
                };
        }
    };

    const categoryContent = getCategoryContent(normalizedCategory);

    return (
        <section className="google-auto-ads-no-ad" style={{ 
            marginBottom: 'clamp(4rem, 10vh, 6rem)', 
            padding: 'clamp(1.5rem, 6vw, 4.5rem)', 
            background: 'linear-gradient(145deg, #ffffff, #f8fafc)', 
            color: '#0f172a', 
            borderRadius: 'clamp(32px, 5vw, 64px)',
            border: '1px solid #e2e8f0',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.03)'
        }}>
            {/* Technical Header Accent */}
            <div style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '6px', 
                background: 'linear-gradient(90deg, #2563eb, #60a5fa, #2563eb)' 
            }} />
            
            {/* Security Badge - Responsive positioning */}
            <div style={{
                position: 'absolute',
                top: 'clamp(1rem, 3vw, 2rem)',
                right: 'clamp(1rem, 3vw, 2rem)',
                padding: '0.4rem 0.85rem',
                background: 'rgba(16, 185, 129, 0.1)',
                color: '#10b981',
                borderRadius: '100px',
                fontSize: '0.65rem',
                fontWeight: 900,
                letterSpacing: '0.1em',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                zIndex: 10
            }}>
                <span style={{ width: '6px', height: '6px', background: '#10b981', borderRadius: '50%' }} />
                VERIFIED
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'clamp(2rem, 5vw, 5rem)' }}>
                <div>
                    <h3 style={{ color: '#0f172a', fontSize: '1.75rem', fontWeight: 900, marginBottom: '2rem', letterSpacing: '-0.03em', paddingRight: '4rem' }}>
                        {categoryContent.heading}
                    </h3>
                    <div style={{ lineHeight: '2.3', color: '#475569', fontSize: '1.15rem', marginBottom: '2.5rem' }}>
                        {categoryContent.body}
                    </div>
                    <p style={{ lineHeight: '2.1', color: '#475569', fontSize: '1.1rem' }}>
                        Running the core logic directly in your browser's memory heap keeps your workflow fast and uninterrupted. You get the raw speed of a compiled C++ binary without needing to configure local environments or deal with backend network lag.
                    </p>
                </div>
                
                <div style={{ 
                    padding: 'clamp(1.5rem, 5vw, 3rem)', 
                    background: '#0f172a', 
                    borderRadius: '32px', 
                    boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.2)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Decorative Background Element */}
                    <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: '150px', height: '150px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
 
                    <h4 style={{ color: '#60a5fa', fontSize: '1.4rem', fontWeight: 900, marginBottom: '2rem', letterSpacing: '-0.02em' }}>
                        Local Verification Protocol
                    </h4>
                    <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '1.75rem', position: 'relative' }}>
                        <li style={{ display: 'flex', gap: '1.25rem' }}>
                            <div style={{ width: '24px', height: '24px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifySelf: 'center', flexShrink: 0, color: '#10b981', fontWeight: 900, fontSize: '0.8rem', justifyContent: 'center' }}>✓</div>
                            <div style={{ fontSize: '1rem', color: '#94a3b8', lineHeight: '1.5' }}>
                                <strong style={{ color: '#f8fafc', display: 'block', marginBottom: '0.25rem' }}>Volatile Memory Isolation</strong>
                                Inputs processed through {toolName} are kept strictly in volatile RAM and never written to permanent disk or database storage.
                            </div>
                        </li>
                        <li style={{ display: 'flex', gap: '1.25rem' }}>
                            <div style={{ width: '24px', height: '24px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifySelf: 'center', flexShrink: 0, color: '#10b981', fontWeight: 900, fontSize: '0.8rem', justifyContent: 'center' }}>✓</div>
                            <div style={{ fontSize: '1rem', color: '#94a3b8', lineHeight: '1.5' }}>
                                <strong style={{ color: '#f8fafc', display: 'block', marginBottom: '0.25rem' }}>Web Worker Parallelism</strong>
                                Multi-threaded background workers handle heavy {toolName} calculations without freezing your active browser UI.
                            </div>
                        </li>
                        <li style={{ display: 'flex', gap: '1.25rem' }}>
                            <div style={{ width: '24px', height: '24px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifySelf: 'center', flexShrink: 0, color: '#10b981', fontWeight: 900, fontSize: '0.8rem', justifyContent: 'center' }}>✓</div>
                            <div style={{ fontSize: '1rem', color: '#94a3b8', lineHeight: '1.5' }}>
                                <strong style={{ color: '#f8fafc', display: 'block', marginBottom: '0.25rem' }}>Total Data Privacy</strong>
                                Executing {toolName} client-side respects GDPR and CCPA mandates by keeping user inputs 100% local.
                            </div>
                        </li>
                        <li style={{ display: 'flex', gap: '1.25rem' }}>
                            <div style={{ width: '24px', height: '24px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifySelf: 'center', flexShrink: 0, color: '#10b981', fontWeight: 900, fontSize: '0.8rem', justifyContent: 'center' }}>✓</div>
                            <div style={{ fontSize: '1rem', color: '#94a3b8', lineHeight: '1.5' }}>
                                <strong style={{ color: '#f8fafc', display: 'block', marginBottom: '0.25rem' }}>High Speed Execution</strong>
                                Direct implementation of optimized browser APIs allows {toolName} outputs to compile instantly with sub-millisecond lag.
                            </div>
                        </li>
                        <li style={{ display: 'flex', gap: '1.25rem' }}>
                            <div style={{ width: '24px', height: '24px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifySelf: 'center', flexShrink: 0, color: '#10b981', fontWeight: 900, fontSize: '0.8rem', justifyContent: 'center' }}>✓</div>
                            <div style={{ fontSize: '1rem', color: '#94a3b8', lineHeight: '1.5' }}>
                                <strong style={{ color: '#f8fafc', display: 'block', marginBottom: '0.25rem' }}>Predictable Outputs</strong>
                                Calculations and transformations in {toolName} run deterministically to produce clean, reproducible results.
                            </div>
                        </li>
                    </ul>
 
                    <div style={{ 
                        marginTop: '3rem', 
                        paddingTop: '2.5rem', 
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        fontSize: '0.9rem',
                        color: '#64748b',
                        lineHeight: '1.8'
                    }}>
                        <div style={{ color: '#60a5fa', fontWeight: 900, marginBottom: '1.25rem', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
                            // Developer Notes
                        </div>
                        {(() => {
                            const getManifesto = () => {
                                switch (normalizedCategory) {
                                    case 'Financial':
                                        return `In modern financial operations, calculation precision and data isolation are critical. By offloading 100% of the math routines to your local device, CorpToolset avoids central tracking of corporate finances or income logs. We rely on browser runtime memory allocations to keep calculations isolated to your current session.`;
                                    case 'Security':
                                        return `In the current threat landscape, credentials and keys must never be processed on shared remote infrastructure. By running 100% of cryptographic operations within your local browser, CorpToolset eliminates remote log injection vectors. We rely on the native Web Crypto API to ensure secure local processing.`;
                                    case 'Developer':
                                        return `For modern development workflows, structural parsers should run close to your codebase. Offloading compilation and formatting tasks to local browser workers prevents backend interception. We leverage clean thread isolation to ensure code structures remain secure.`;
                                    case 'Content':
                                        return `In digital publishing, text manipulation needs to be quick and private. By processing documents and string payloads locally, CorpToolset ensures that internal documentation is never uploaded or cached on public infrastructure. Data remains strictly in transient memory.`;
                                    case 'Marketing':
                                        return `Structuring strategy requires protecting business assets and brand plans. CorpToolset runs logic parsing locally to keep strategy drafts and personas off public training sets, securing brand planning in your current browser tab.`;
                                    case 'PDF':
                                        return `In document workflows, sharing confidential files with remote servers compromises security. By orchestrating file parsing and stream splitting locally within transient memory arrays, CorpToolset guarantees document isolation. All PDF manipulations execute directly in your active browser session.`;
                                    default:
                                        return `Privacy is a strict engineering constraint, not a legal afterthought. By offloading 100% of the computational workload to your local device, CorpToolset eliminates central points of failure. We rely on transient in-memory buffers to keep operations isolated and clean.`;
                                }
                            };

                            const getTechMeta = () => {
                                switch (normalizedCategory) {
                                    case 'Financial':
                                        return { ctx: 'IEEE 754 Float Arithmetic', seg: 'LOCAL_FLOAT_STACK', parity: 'DETERMINISTIC', persistence: 'ZERO_TRACE' };
                                    case 'Security':
                                        return { ctx: 'AES-GCM-256 / WebCrypto API', seg: 'SECURE_RAM_HEAP', parity: 'DETERMINISTIC', persistence: 'ZERO_TRACE' };
                                    case 'Developer':
                                        return { ctx: 'Local AST Parse Engine', seg: 'WORKER_STACK', parity: 'DETERMINISTIC', persistence: 'ZERO_TRACE' };
                                    case 'Content':
                                        return { ctx: 'Browser String Buffer/UTF-8', seg: 'LOCAL_STRING_STACK', parity: 'DETERMINISTIC', persistence: 'ZERO_TRACE' };
                                    case 'Marketing':
                                        return { ctx: 'Linguistic Weighting Sandbox', seg: 'SESSION_HEAP', parity: 'DETERMINISTIC', persistence: 'ZERO_TRACE' };
                                    case 'PDF':
                                        return { ctx: 'Isolated WASM Primitives', seg: 'ARRAYBUFFER_HEAP', parity: 'DETERMINISTIC', persistence: 'ZERO_TRACE' };
                                    default:
                                        return { ctx: 'Browser Sandboxing', seg: 'ISOLATED_SESSION', parity: 'DETERMINISTIC', persistence: 'ZERO_TRACE' };
                                }
                            };

                            const manifesto = getManifesto();
                            const meta = getTechMeta();

                            return (
                                <>
                                    <p style={{ marginBottom: '1.5rem', color: '#f8fafc' }}>
                                        {manifesto}
                                    </p>
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '1fr 1fr', 
                                        gap: '1rem', 
                                        padding: '1.25rem', 
                                        background: 'rgba(255,255,255,0.02)', 
                                        borderRadius: '16px',
                                        border: '1px solid rgba(255,255,255,0.05)',
                                        fontSize: '0.75rem',
                                        fontFamily: 'monospace'
                                    }}>
                                        <div>
                                            <span style={{ color: '#475569', display: 'block' }}>PROCESSING_CTX</span>
                                            <span style={{ color: '#f8fafc' }}>{meta.ctx}</span>
                                        </div>
                                        <div>
                                            <span style={{ color: '#475569', display: 'block' }}>MEMORY_SEGMENT</span>
                                            <span style={{ color: '#f8fafc' }}>{meta.seg}</span>
                                        </div>
                                        <div>
                                            <span style={{ color: '#475569', display: 'block' }}>LOGIC_PARITY</span>
                                            <span style={{ color: '#f8fafc' }}>{meta.parity}</span>
                                        </div>
                                        <div>
                                            <span style={{ color: '#475569', display: 'block' }}>PERSISTENCE</span>
                                            <span style={{ color: '#f8fafc' }}>{meta.persistence}</span>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>

            <div style={{ 
                marginTop: 'clamp(3rem, 6vh, 5rem)', 
                background: '#ffffff', 
                borderRadius: '32px', 
                padding: 'clamp(1.5rem, 5vw, 3.5rem)', 
                border: '1px solid #f1f5f9',
                boxShadow: '0 10px 30px -10px rgba(0,0,0,0.02)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem', flexWrap: 'wrap', gap: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ width: '12px', height: '12px', background: '#3b82f6', borderRadius: '50%' }} />
                        <h4 style={{ color: '#0f172a', fontSize: '1.4rem', fontWeight: 900, margin: 0 }}>{toolName} Technical Details</h4>
                    </div>
                    <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#64748b', 
                        fontFamily: 'monospace', 
                        background: '#f8fafc', 
                        padding: '0.6rem 1.25rem', 
                        borderRadius: '100px',
                        border: '1px solid #e2e8f0',
                        fontWeight: 700
                    }}>
                        SEC_ID: CT-{(toolName || 'Utility').replace(/\s+/g, '').toUpperCase()}-2026-X
                    </div>
                </div>
                <div style={{ overflowX: 'auto', width: '100%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', color: '#475569', fontSize: '1rem', minWidth: '400px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                                <th style={{ padding: '1.25rem 1rem', textAlign: 'left', color: '#0f172a', fontWeight: 900, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.05em' }}>Protocol Layer</th>
                                <th style={{ padding: '1.25rem 1rem', textAlign: 'left', color: '#0f172a', fontWeight: 900, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.05em' }}>Technical Specification</th>
                                <th style={{ padding: '1.25rem 1rem', textAlign: 'left', color: '#0f172a', fontWeight: 900, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.05em' }}>Compliance Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categoryContent.specs.map((spec, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }} className="hover-lift-subtle">
                                    <td style={{ padding: '1.25rem 1rem', fontWeight: 800, color: '#1e293b' }}>{spec.layer}</td>
                                    <td style={{ padding: '1.25rem 1rem', color: '#64748b' }}>{spec.tech}</td>
                                    <td style={{ padding: '1.25rem 1rem' }}>
                                        <span style={{ 
                                            background: 'rgba(16, 185, 129, 0.1)', 
                                            color: '#10b981', 
                                            padding: '0.4rem 1rem', 
                                            borderRadius: '100px', 
                                            fontSize: '0.85rem', 
                                            fontWeight: 800,
                                            border: '1px solid rgba(16, 185, 129, 0.15)'
                                        }}>
                                            {spec.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};

export default InstitutionalShield;
